import { useState } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function BrandContributionForm({ businessId, businessName, onSuccess, onCancel }) {
  const [description, setDescription] = useState("");
  const [yearFounded, setYearFounded] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [twitter, setTwitter] = useState("");
  const [facebook, setFacebook] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim() && !yearFounded && !website.trim()) {
      toast.error("Please fill in at least one field");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/brand-contributions`, {
        business_id: businessId,
        description: description.trim(),
        year_founded: yearFounded ? parseInt(yearFounded, 10) : null,
        website: website.trim(),
        instagram: instagram.trim(),
        twitter: twitter.trim(),
        facebook: facebook.trim(),
      });
      toast.success("Thanks! Your contribution is under review.");
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="brand-contribution-form">
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={onCancel}
          className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
          data-testid="contribution-back-btn"
        >
          <ArrowLeft className="w-4 h-4 text-slate-500" />
        </button>
        <div>
          <h3 className="text-base font-bold text-slate-800">Contribute Brand Info</h3>
          <p className="text-[10px] text-slate-400">{businessName}</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this brand known for?"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-200"
            rows={3}
            maxLength={500}
            data-testid="contribution-description"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Year Founded</label>
          <input
            type="number"
            value={yearFounded}
            onChange={(e) => setYearFounded(e.target.value)}
            placeholder="e.g. 1987"
            min={1800}
            max={new Date().getFullYear()}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
            data-testid="contribution-year"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Website</label>
          <input
            type="text"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://example.com"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
            data-testid="contribution-website"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] font-medium text-slate-500 mb-1 block">Instagram</label>
            <input
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@handle"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="contribution-instagram"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-slate-500 mb-1 block">X / Twitter</label>
            <input
              type="text"
              value={twitter}
              onChange={(e) => setTwitter(e.target.value)}
              placeholder="@handle"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="contribution-twitter"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-slate-500 mb-1 block">Facebook</label>
            <input
              type="text"
              value={facebook}
              onChange={(e) => setFacebook(e.target.value)}
              placeholder="page name"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="contribution-facebook"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-5 w-full py-2.5 rounded-xl bg-amber-500 text-white font-bold text-sm shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="contribution-submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for Review"}
      </button>
    </form>
  );
}
